import { useState } from 'react';
import type { ITag } from '@map-bldr/shared';
import { TagListItem } from './TagListItem';
import { useTags, useCreateTag, useDeleteTag, useUpdateTag } from '../hooks/tagHooks';

export interface ITagListProps {}

export function TagList({}: ITagListProps) {
  const { data: tags = [], isLoading, error } = useTags();
  const createTag = useCreateTag();
  const deleteTag = useDeleteTag();
  const updateTag = useUpdateTag();

  const [isAdding, setIsAdding] = useState(false);

  const handleSaveNewTag = (name: string, description?: string) => {
    const id = crypto.randomUUID();
    createTag.mutate({
      id,
      name,
      description: description ?? '',
    });
    setIsAdding(false);
  };

  const handleRemoveTag = (id: string) => {
    deleteTag.mutate(id);
  };

  const handleUpdateTag = (id: string, updates: Partial<ITag>) => {
    updateTag.mutate({
      tagId: id,
      data: {
        name: updates.name,
        description: updates.description,
      },
    });
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-muted-foreground font-mono text-sm">Loading tags...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-red-500 font-mono text-sm">Error loading tags: {error.message}</p>
      </div>
    );
  }

  const sortedTags = [...tags].reverse();

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        {tags.length === 0 && !isAdding ? (
          <p className="text-muted-foreground italic font-mono text-sm">
            $ Add tags to organize your locations
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {sortedTags.map((tag, index) => (
              <TagListItem
                key={tag.id}
                tag={tag}
                index={index}
                onRemove={handleRemoveTag}
                onUpdate={handleUpdateTag}
              />
            ))}
            {isAdding && (
              <TagListItem
                tag={{
                  id: 'new',
                  name: '',
                  description: '',
                  created_at: new Date().toISOString(),
                }}
                index={tags.length}
                onRemove={handleCancelAdd}
                onUpdate={(_, updates) => {
                  handleSaveNewTag(
                    updates.name || '',
                    updates.description
                  );
                }}
                isNewTag
              />
            )}
          </div>
        )}
      </div>
      <div className="border-t border-border p-4 bg-card">
        <button
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
          className="w-full bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground border-none rounded-md px-4 py-2 text-sm font-bold cursor-pointer disabled:cursor-not-allowed font-mono hover:opacity-90 transition-opacity"
        >
          Add Tag +
        </button>
      </div>
    </div>
  );
}
